import React, { useMemo, useState } from "react";
import { Box, Typography, Link, Divider, Button } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import "../../Footer/Footer.css";
import { ASSETS_BASE_URL, COMPANY_ADDRESS, COMPANY_SOCIAL1, COMPANY_SOCIAL2, COMPANY_SOCIAL3, COMPANY_SOCIAL4 } from "../../../config";
import { useTranslation } from "react-i18next";
import Language from "../../../Language/language";
import { LABELS, UI_TEXT } from "../../../constants";
import { useNavigate } from "react-router-dom";
import { FaFacebook, FaLinkedin, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { BiPhoneCall } from "react-icons/bi";
import { useCompliance } from "../../../context/complianceContext";
import ComplianceDescriptionModal from "../../CompliancePolicy/ComplianceDescriptionModal";
import EulaLibraryModal from "../../CompliancePolicy/EulaLibraryModal";
import { openComplianceDocument } from "../../CompliancePolicy/complianceUtils";
import useBrandingAssets from "../../../hooks/useBrandingAssets";
import CompanyLogo from "../../CompanyLogo/CompanyLogo";
import useCompanyInfo from "../../../hooks/useCompanyInfo";
import { useOptions } from "../../../context/optionContext";
import { isRsplFeatureEnabled } from "../../../utilities/rspl";

const Footer = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { items } = useCompliance();
    const { footerLogoEnabled } = useBrandingAssets();
    const { companyName, companyEmail, companyAddress, companyPhone, companySocial1, companySocial2, companySocial3, companySocial4 } = useCompanyInfo();
    const { optionKeys } = useOptions();
    const showLanguageSelector = optionKeys?.enableLanguageTranslation !== "false";
    const showFooterVersion = optionKeys?.footerShowVersion !== "false";
    const showFooterSupport = optionKeys?.footerShowSupport !== "false";
    const showFooterTerms = optionKeys?.footerShowTerms !== "false";
    const showFooterEula = optionKeys?.footerShowEula !== "false";
    const showFooterPrivacy = optionKeys?.footerShowPrivacy !== "false";
    const showFooterCookiePolicy = optionKeys?.footerShowCookiePolicy !== "false";
    const showFooterManual = optionKeys?.footerShowManual !== "false";
    const rsplFeatureEnabled = isRsplFeatureEnabled(optionKeys);
    const eulaDocs = items.EULA || [];
    const privacyDoc = items.PRIVACY || null;
    const termsDoc = items.TERMS || null;
    const supportDoc = items.SUPPORT || null;
    const cookieDoc = items.COOKIE || null;
    const [policyModal, setPolicyModal] = useState({ open: false, doc: null });
    const [eulaModalOpen, setEulaModalOpen] = useState(false);
    const quickLinks = [
        { label: t('LABELS.HOME'), path: "/" },
        { label: t('LABELS.MACHINESS'), path: "/machines" },
        ...(rsplFeatureEnabled ? [{ label: t('LABELS.RSPL'), path: "/rspl" }] : []),
        { label: t('LABELS.RECOMMENDATION'), path: "/recommendations" },
    ];

    const quickActions = [
        { label: t('LABELS.SEARCH'), path: "/search-results" },
        { label: t('LABELS.TRACK_ORDER'), path: "/track-quote" },
        // { label: t('LABELS.CONTACT_CCR'), path: "/support" },
    ]

    const legalLinks = useMemo(
        () => [
            { id: "support", label: t('LABELS.SUPPORT'), doc: supportDoc, fallback: "" },
            { id: "terms", label: t('TITLES.TERMS_AND_CONDITIONS'), doc: termsDoc, fallback: "" },
            { id: "privacy", label: t('LABELS.PRIVACY_POLICY'), doc: privacyDoc, fallback: "" },
            { id: "cookie", label: t('LABELS.COOKIE_POLICY', { defaultValue: 'Cookie Policy' }), doc: cookieDoc, fallback: "" },
        ],
        [supportDoc, termsDoc, privacyDoc, cookieDoc, t]
    );

    const visibleLegalLinks = useMemo(
        () =>
            legalLinks.filter((link) => {
                if (link.id === "support") return showFooterSupport;
                if (link.id === "terms") return showFooterTerms;
                if (link.id === "privacy") return showFooterPrivacy;
                if (link.id === "cookie") return showFooterCookiePolicy;
                return true;
            }),
        [legalLinks, showFooterTerms, showFooterPrivacy, showFooterSupport, showFooterCookiePolicy]
    );

    const handlePolicyClick = (item) => {
        openComplianceDocument({
            document: item.doc,
            fallbackUrl: item.fallback,
            onShowDescription: (document) =>
                setPolicyModal({ open: true, doc: document }),
        });
    };

    const handleEulaClick = () => {
        if (!eulaDocs.length) {
            openComplianceDocument({
                document: null,
                fallbackUrl: "",
                onShowDescription: (document) =>
                    setPolicyModal({ open: true, doc: document }),
            });
            return;
        }
        if (eulaDocs.length === 1) {
            openComplianceDocument({
                document: eulaDocs[0],
                fallbackUrl: "",
                onShowDescription: (document) =>
                    setPolicyModal({ open: true, doc: document }),
            });
            return;
        }
        setEulaModalOpen(true);
    };

    const contactInfo = [
        { icon: <LocationOnIcon className="footer-icon" />, text: companyAddress || COMPANY_ADDRESS },
        { icon: <PhoneIcon className="footer-icon" />, text: companyPhone, isPhone: true },
        { icon: <EmailIcon className="footer-icon" />, text: companyEmail, isEmail: true },
    ];

    const REGION_PHONE_NUMBERS = [
        { regionKey: "FOOTER.REGIONS.NORTH_AMERICA", phone: "(+1) 804-342-9700" },
        { regionKey: "FOOTER.REGIONS.GREATER_ASIA", phone: "(+65) 8128-4044" },
        { regionKey: "FOOTER.REGIONS.CHINA", phone: "(+86) 150-2244-2472" },
        { regionKey: "FOOTER.REGIONS.EMEA", phone: "(+31) 183-627-555" },
        { regionKey: "FOOTER.REGIONS.LATAM", phone: "(+52) 229-915-3501" },
    ];

    const socialIcons = [
        {
            name: t('SOCIAL_MEDIA.FACEBOOK'),
            icon: <FaFacebook />,
            href: companySocial1 || COMPANY_SOCIAL1
        },
        {
            name: t('SOCIAL_MEDIA.LINKEDIN'),
            icon: <FaLinkedin />,
            href: companySocial2 || COMPANY_SOCIAL2
        },
        {
            name: t('SOCIAL_MEDIA.TWITTER'),
            icon: <FaXTwitter />,
            href: companySocial3 || COMPANY_SOCIAL3
        },
        {
            name: t('SOCIAL_MEDIA.YOUTUBE'),
            icon: <FaYoutube />,
            href: companySocial4 || COMPANY_SOCIAL4
        }
    ];


    return (
        <Box className="footer-main" component="footer" pt={4} pb={1} px={2} sx={{}}>
            <Box display="flex" sx={{ width: '100%', alignItems: 'flex-start', justifyContent: 'center', columnGap: 2, px: 2 }}>
                {/* Logo and Description */}
                <Box sx={{ width: '250px' }} mb={2}>
                    <Box display="flex" sx={{ justifyContent: "center", rowGap: 2 }} flexDirection="column" alignItems="center">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: 'auto', width: '150px' }}>
                            <CompanyLogo alt="logo" onClick={() => navigate("/home")} style={{ cursor: "pointer", maxWidth: "140px" }} skeletonWidth={140} skeletonHeight={40} />
                        </Box>
                        <Box display={"flex"} columnGap={1} justifyContent={"center"} alignItems={"center"}>
                            {socialIcons.map((social, index) => (
                                <Button
                                    key={index}
                                    variant="roundedGreyBgSquareContained"
                                    color="#aaa"
                                    component="a"
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={social.name}
                                >
                                    {social.icon}
                                </Button>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', height: 'auto', width: '85px' }}>
                            {footerLogoEnabled && (
                                <CompanyLogo variant="secondary" alt="logo" onClick={() => navigate("/home")} style={{ cursor: "pointer", maxWidth: "80px" }} skeletonWidth={80} skeletonHeight={30} />
                            )}
                        </Box>
                    </Box>
                </Box>


                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'flex-start',
                        rowGap: 2,
                        width: '100%',
                        pb: 1.5,
                    }}
                >
                    <Typography variant="h5" className="footer-title" textAlign="center">
                        {t('FOOTER.TAGLINE')}
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            width: '100%',
                            columnGap: 3,
                        }}
                    >
                        {REGION_PHONE_NUMBERS.map((info) => (
                            <Box
                                key={info.regionKey}
                                sx={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-start',
                                    rowGap: 0.5,
                                    width: '100%',
                                }}
                            >
                                <Divider flexItem />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="h6"
                                        fontWeight={'bold'}
                                        textTransform={'uppercase'}
                                    >
                                        {t(info.regionKey)}
                                    </Typography>
                                    <BiPhoneCall />
                                </Box>

                                {/* Phone number as a clickable tel link */}
                                <Link
                                    href={`tel:${info.phone}`}
                                    underline="hover"
                                    color="primary"
                                    sx={{ textTransform: 'uppercase' }}
                                >
                                    <Typography variant="h6">{info.phone}</Typography>
                                </Link>
                            </Box>
                        ))}
                    </Box>

                    <Box
                        sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'flex-start',
                            alignItems: 'flex-start',
                            rowGap: 0.5,
                            width: '100%',
                        }}
                    >
                        <Typography
                            variant="body2"
                            fontSize={'11px'}
                            color="text.secondary"
                            fontWeight={600}
                            textTransform={'uppercase'}
                        >
                            {t('FOOTER.CONFIDENTIAL_NOTICE_TITLE')}
                        </Typography>
                        <Typography variant="body2" fontSize={'10px'} color="text.secondary">
                            {t('FOOTER.CONFIDENTIAL_NOTICE_BODY', { company: companyName })}
                        </Typography>
                    </Box>
                </Box>



            </Box>

            {/* Divider and Copyright */}
            <Divider sx={{ mb: 2 }} variant="middle" />
            <Box textAlign="center" sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', columnGap: 1, mb: 1 }}>
                <Typography variant="body2" color="textSecondary">
                    {t('FOOTER.ALL_RIGHTS_RESERVED', { year: new Date().getFullYear(), company: companyName })}
                </Typography>
                {(() => {
                    const footerItems = [
                        showFooterVersion && {
                            id: "version",
                            node: (
                                <Link color="textSecondary" underline="hover" href={"/admin?tab=version_info"}>
                                    {t('LABELS.VERSION')}
                                </Link>
                            ),
                        },
                        ...visibleLegalLinks.map((item) => ({
                            id: `legal-${item.id}`,
                            node: (
                                <Link
                                    component="button"
                                    type="button"
                                    color="textSecondary"
                                    underline="hover"
                                    onClick={() => handlePolicyClick(item)}
                                >
                                    {item.label}
                                </Link>
                            ),
                        })),
                        showFooterEula && {
                            id: "eula",
                            node: (
                                <Link
                                    component="button"
                                    type="button"
                                    color="textSecondary"
                                    underline="hover"
                                    onClick={handleEulaClick}
                                >
                                    {t('LABELS.EULA')}
                                </Link>
                            ),
                        },
                        showFooterManual && {
                            id: "manual",
                            node: (
                                <Link color="textSecondary" underline="hover" href={`${ASSETS_BASE_URL}/manual.pdf`}>
                                    {t('LABELS.MANUAL')}
                                </Link>
                            ),
                        },
                    ].filter(Boolean);

                    return footerItems.map((item, index) => (
                        <React.Fragment key={item.id}>
                            <Typography variant="body2" color="textSecondary">
                                {index === 0 ? "" : "| "}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                {item.node}
                            </Typography>
                        </React.Fragment>
                    ));
                })()}
                {showLanguageSelector && (
                    <>
                        <Typography variant="body2" color="textSecondary">|</Typography>
                        <Language />
                    </>
                )}
            </Box>
            <ComplianceDescriptionModal
                open={policyModal.open}
                setOpen={(open) =>
                    setPolicyModal((prev) => ({ open, doc: open ? prev.doc : null }))
                }
                document={policyModal.doc}
            />
            <EulaLibraryModal
                open={eulaModalOpen}
                setOpen={setEulaModalOpen}
                documents={eulaDocs}
            />
        </Box>
    );
};

export default Footer;
